import {
  CompressOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Col, Pagination, Row, Select, Spin, Table, Tag, Tooltip } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompanies } from '../../contextRedux/Company/CompanyStore';
import { setPmOfferProjects, updateProps } from '../../contextRedux/PMOfferProject/PmOfferModelProject';
import { setPmOfferStatuses } from '../../contextRedux/PmOfferStatus/PmOfferStatusStore';
import { SearchInput } from '../../customComponents/input/SearchInput';
import { PmOffer } from '../../DTOS/PmOffer/Response/PmOffer';
import CompanyService from '../../services/company/CompanyService';
import ConfigurationService from '../../services/configuration/ConfigurationService';
import PmOfferProjectService from '../../services/pmOffer/PmOfferService';
import { RootState } from '../../store';
import PmOfferInformation from '../PmOffer/PmOfferInformation';
import './NewPmOfferProject/Tabs/ReviewTab/ReviewTab.css';

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const PmOfferProject: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.pmOfferProject.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.pmOfferProject.props.skip);
  const take = useSelector((state: RootState) => state.models?.pmOfferProject.props.take);
  const refresh = useSelector((state: RootState) => state.models?.pmOfferProject.props.refresh);
  const companies = useSelector((state: RootState) => state?.store?.company?.data ?? []);
  const pmOfferProjectStatuses = useSelector((state: RootState) => state?.store?.pmOfferProject?.data ?? []);

  const reduxPmOffer = useSelector((state: RootState) => state.models?.pmOfferProject);
  //const pmOffers = useSelector((state: RootState) => state.store?.pmOfferProject.data ?? []);

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<PmOffer | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [visibleMenuRecord, setVisibleMenuRecord] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  const currentPage = Math.floor(reduxPmOffer.props.skip / reduxPmOffer.props.take) + 1;
  const { message } = App.useApp();
  const pageSizes = [10, 20, 30, 50];

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setVisibleMenuRecord(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const handleMenuVisibility = (recordId: string) => {
    setVisibleMenuRecord(visibleMenuRecord === recordId ? null : recordId);
  };

  const handleOptionClick = (action: () => void) => {
    action();
    setVisibleMenuRecord(null);
  };

  const handleModalInformationOpen = (record: PmOffer) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  const {
    data: _dates,
    refetch,
    isFetching: isPmOffersLoading,
  } = useQuery({
    queryKey: ['pmOffers', searchTerm, skip, take, refresh, sortField, sortOrder, selectedCompany, selectedStatus],
    queryFn: () =>
      PmOfferProjectService.search({
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        termen: searchTerm || undefined,
        selectedCompany: selectedCompany,
        selectedStatus: selectedStatus,
        pmOfferTypeIds: [2],
        skip,
        take,
        sortField,
        sortOrder: sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 })); // Update total items
        dispatch(setPmOfferProjects(data?.response));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _offerStatuses, isFetching: isPmOfferProjectStatusesLoading } = useQuery({
    queryKey: ['pmOfferProjectStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmOfferStatus({}).then((data) => {
        dispatch(setPmOfferStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const { mutate: printPdfPmOffer, isPending: isPrintingPmOffer } = useMutation({
    mutationFn: (record: PmOffer) =>
      PmOfferProjectService.print({
        ids: [record.id],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        isLaborExtraIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const printToPdf = (record: PmOffer) => {
    printPdfPmOffer(record);
  };

  const { mutate: clonePdfPmOffer, isPending: isCloningPmOffer } = useMutation({
    mutationFn: (record: PmOffer) => PmOfferProjectService.clone(record?.id),
    onSuccess: (response: any) => {
      message.success(response.response?.number + ' a fost clonat cu success.');
      refetch();
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const clone = (record: PmOffer) => {
    clonePdfPmOffer(record);
  };

  const handleCompanyChange = (value: string | undefined) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleStatusChange = (value: string | undefined) => {
    setSelectedStatus(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxPmOffer.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: any[] = [
    {
      title: 'Oferta Id',
      dataIndex: 'id',
      key: 'id',
      hidden: true,
    },
    {
      title: 'Numar',
      dataIndex: 'number',
      key: 'number',
      render: (number: string) => (
        <td data-label='Numar'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{number}</Tag>
          </div>
        </td>
      ),
    },
    {
      title: 'Client',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{company?.name}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Sisteme',
      dataIndex: '',
      key: 'systemTypes',
      render: (record: any) => {
        const pmOfferWorkPlaceItems = record.pmOfferWorkPlaceItems ?? [];

        const uniqueSystemTypeNames = Array.from(
          new Set(
            pmOfferWorkPlaceItems.map((item: any) => item.systemType?.name).filter((name: string | undefined) => !!name)
          )
        );

        const displayCount = 3;
        const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
        const displayNames = shouldShowEllipsis ? uniqueSystemTypeNames.slice(0, displayCount) : uniqueSystemTypeNames;
        const fullList = uniqueSystemTypeNames.join(', ');

        return (
          <Tooltip title={fullList}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxWidth: '100%',
              }}
            >
              {displayNames.map((name: any, index: any) => (
                <Tag color='blue' key={index}>
                  {name}
                </Tag>
              ))}
              {shouldShowEllipsis && <Tag color='blue'>...</Tag>}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Data Oferta',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      render: (date: string) => (
        <td data-label='Data Oferta'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{moment(date).format('DD-MM-YYYY')}</Tag>
          </div>
        </td>
      ),
    },
    {
      title: 'Data Inceput',
      dataIndex: 'dateStart',
      key: 'dateStart',
      sorter: true,
      render: (record: string) => moment(record).format('DD-MM-YYYY'),
    },
    {
      title: 'Data Sfarsit',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      sorter: true,
      render: (record: string) => moment(record).format('DD-MM-YYYY'),
    },
    {
      title: 'Status',
      dataIndex: ['enumPmOfferStatus', 'name'],
      key: 'enumOfferStatus',
      render: (record: string) => (
        <td data-label='Status'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'purple'}>{record}</Tag>
          </div>
        </td>
      ),
    },
    {
      title: 'Actiune',
      dataIndex: '',
      key: 'actions',
      render: (record: any) => {
        const overlay = (
          <div className='circle-menu'>
            <Tooltip title='Modifica'>
              <Button
                className='circle-menu-item'
                icon={<EditOutlined />}
                onClick={() =>
                  handleOptionClick(() =>
                    window.open(`/project-management/pmOfferProject/edit?id=${record?.id}`, '_blank')
                  )
                }
              />
            </Tooltip>
            <Tooltip title='Informatii'>
              <Button
                className='circle-menu-item'
                icon={<InfoCircleOutlined />}
                onClick={() => handleOptionClick(() => handleModalInformationOpen(record))}
              />
            </Tooltip>
            <Tooltip title='Duplica'>
              <Button
                className='circle-menu-item'
                icon={<CopyOutlined />}
                onClick={() => handleOptionClick(() => clone(record))}
              />
            </Tooltip>
            <Tooltip title='Descarca PDF'>
              <Button
                className='circle-menu-item'
                icon={<DownloadOutlined />}
                onClick={() => handleOptionClick(() => printToPdf(record))}
              />
            </Tooltip>
          </div>
        );

        return (
          <div>
            {visibleMenuRecord === record.id && overlay}
            <Button onClick={() => handleMenuVisibility(record.id)} className='plus_edit'>
              {visibleMenuRecord === record.id ? <CompressOutlined /> : <PlusOutlined />}
            </Button>
          </div>
        );
      },
      width: 'auto',
      fixed: 'right',
    },
  ];

  if (
    isPmOffersLoading ||
    isPmOffersLoading ||
    isPrintingPmOffer ||
    isCloningPmOffer ||
    isCompaniesLoading ||
    isPmOfferProjectStatusesLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <Row justify='space-between' align='middle' style={{ marginBottom: '20px' }}>
        <Col>
          <h1>Oferte Proiect</h1>
        </Col>

        <Col>
          <Tooltip title='Creeaza oferta' trigger={'hover'} placement='left'>
            <Button
              type='primary'
              onClick={() => window.open(`/project-management/pmOfferProject/new`, '_blank')}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <SearchInput
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e: any) => handleSearch(e.target.value)}
            autoFocus={true}
            style={{ width: '100%' }} // Ensures input takes full width
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa status'
            value={selectedStatus}
            onChange={handleStatusChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {pmOfferProjectStatuses?.map((status: any) => (
              <Select.Option key={status?.id} value={status?.name}>
                {status?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <PmOfferInformation
        handleModalInformationClose={handleModalInformationClose}
        modalInformation={modalInformation}
        selectedRecord={selectedRecord}
      />

      {isMobile ? (
        <div className='table-mobile-view'>
          {_dates.map((pmOfferProject: any) => (
            <div className='mobile-view-background' key={pmOfferProject.id}>
              <div className='mobile-view-header'>
                <Tag color='purple'>{pmOfferProject.number}</Tag>
                <Button
                  onClick={() => handleMenuVisibility(pmOfferProject.id)}
                  shape='circle'
                  icon={visibleMenuRecord === pmOfferProject.id ? <CompressOutlined /> : <PlusOutlined />}
                />
              </div>

              {/* Offer Details */}
              <Col span={24} className='CardMobile-item'>
                <strong>Client:</strong>
                <Tooltip title={pmOfferProject.company?.name}>
                  <Tag className='client-tag' color='blue'>
                    {' '}
                    {pmOfferProject.company?.name}
                  </Tag>
                </Tooltip>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong>Data Oferta:</strong>
                <Tag color={'blue'}>{moment(pmOfferProject.date).format('DD-MM-YYYY')}</Tag>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong>Data Inceput:</strong>
                {moment(pmOfferProject.dateStart).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong>Data Sfarsit:</strong>
                {moment(pmOfferProject.dateEnd).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Sisteme:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'end' }}>
                  {(() => {
                    const uniqueSystemTypeNames = Array.from(
                      new Set(
                        (pmOfferProject.pmOfferWorkPlaceItems ?? [])
                          .map((item: any) => item.systemType?.name)
                          .filter((name: string | undefined) => !!name)
                      )
                    );

                    const displayCount = 3;
                    const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
                    const displayNames = shouldShowEllipsis
                      ? uniqueSystemTypeNames.slice(0, displayCount)
                      : uniqueSystemTypeNames;
                    const fullList = uniqueSystemTypeNames.join(', ');

                    return (
                      <Tooltip title={fullList}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {displayNames.map((name: any, index: any) => (
                            <Tag
                              key={index}
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              {name}
                            </Tag>
                          ))}
                          {shouldShowEllipsis && (
                            <Tag
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              ...
                            </Tag>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })()}
                </div>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong>Status:</strong>
                <Tag color='green'>{pmOfferProject.enumPmOfferStatus?.name}</Tag>
              </Col>

              {/* Action Buttons */}
              {visibleMenuRecord === pmOfferProject.id && (
                <div className='visible-menu-action-btn'>
                  <Tooltip title='Modifica'>
                    <Button
                      className='modify'
                      shape='circle'
                      icon={<EditOutlined />}
                      onClick={() =>
                        window.open(`/project-management/pmOfferProject/edit?id=${pmOfferProject.id}`, '_blank')
                      }
                    />
                  </Tooltip>
                  <Tooltip title='Informatii'>
                    <Button
                      className='information'
                      shape='circle'
                      icon={<InfoCircleOutlined />}
                      onClick={() => handleModalInformationOpen(pmOfferProject)}
                    />
                  </Tooltip>
                  <Tooltip title='Descarca PDF'>
                    <Button
                      className='pdfDownload'
                      shape='circle'
                      icon={<DownloadOutlined />}
                      onClick={() => printToPdf(pmOfferProject)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}

          {/* Pagination for Mobile */}
          <Pagination
            className='mobile-view-pagination'
            current={currentPage}
            pageSize={take}
            total={reduxPmOffer.props.totalItems}
            onChange={(page, pageSize) => {
              dispatch(updateProps({ field: 'skip', value: (page - 1) * pageSize }));
              dispatch(updateProps({ field: 'take', value: pageSize }));
            }}
            showSizeChanger={true}
            pageSizeOptions={pageSizes.map((size) => size.toString())}
            onShowSizeChange={(_current, size) => {
              dispatch(updateProps({ field: 'skip', value: 0 }));
              dispatch(updateProps({ field: 'take', value: size }));
            }}
            showLessItems
          />
        </div>
      ) : (
        // Desktop View (Table)
        <Table
          dataSource={_dates}
          pagination={paginationConfig}
          columns={columns}
          rowKey='id'
          scroll={{ x: true }}
          onChange={(_pagination: any, _filters: any, sorter: any) => {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
          }}
        />
      )}
    </>
  );
};

export default PmOfferProject;
